/**
 * Project setup detection used by `pull` and `doctor`.
 *
 * The CLI should not guess blindly after it downloads a site. These helpers
 * inspect the files that are actually present and turn them into concrete next
 * steps plus a small set of reproducibility diagnostics.
 *
 * @module
 */
import { Console, Effect, FileSystem, Path } from "effect"
import { OperationFailed, type UsageError } from "./errors"
import { resolveProjectDir } from "./project"

export type PackageManagerName = "pnpm" | "npm" | "yarn" | "bun"
export type DoctorStatus = "ok" | "warn" | "fail"

export interface LockfileInfo {
  readonly name: PackageManagerName
  readonly file: string
}

export interface PackageJsonShape {
  readonly packageManager?: unknown
  readonly engines?: unknown
  readonly scripts?: unknown
  readonly dependencies?: unknown
  readonly devDependencies?: unknown
  readonly overrides?: unknown
  readonly pnpm?: unknown
}

export interface PackageManagerDetection {
  readonly name: PackageManagerName | null
  readonly source: string
  readonly packageManagerField: string | null
  readonly lockfiles: ReadonlyArray<LockfileInfo>
  readonly warnings: ReadonlyArray<string>
}

export interface NodeRequirement {
  readonly range: string | null
  readonly current: string
  readonly satisfied: boolean | null
}

export interface PnpmWorkspaceInfo {
  readonly exists: boolean
  readonly allowBuilds: Readonly<Record<string, boolean>>
}

export interface InstalledVersionInfo {
  readonly astro: string | null
  readonly vite: string | null
}

export interface ProjectSetupInfo {
  readonly projectDir: string
  readonly packageJson: PackageJsonShape | null
  readonly packageJsonParseError: string | null
  readonly packageManager: PackageManagerDetection
  readonly nodeRequirement: NodeRequirement
  readonly hasNodeModules: boolean
  readonly hasSessionFile: boolean
  readonly gitignoreHasHeysegment: boolean
  readonly pnpmWorkspace: PnpmWorkspaceInfo
  readonly neededPnpmBuildApprovals: ReadonlyArray<string>
  readonly installedVersions: InstalledVersionInfo
}

export interface DoctorCheck {
  readonly name: string
  readonly status: DoctorStatus
  readonly summary: string
  readonly details: ReadonlyArray<string>
}

const LOCKFILES: ReadonlyArray<LockfileInfo> = [
  { name: "pnpm", file: "pnpm-lock.yaml" },
  { name: "npm", file: "package-lock.json" },
  { name: "yarn", file: "yarn.lock" },
  { name: "bun", file: "bun.lock" },
  { name: "bun", file: "bun.lockb" },
]

const PACKAGE_MANAGER_PRIORITY: ReadonlyArray<PackageManagerName> = [
  "pnpm",
  "npm",
  "yarn",
  "bun",
]

const PNPM_BUILD_PACKAGES = ["esbuild", "sharp", "workerd"] as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const stringField = (value: unknown, key: string): string | null => {
  if (!isRecord(value)) return null
  const field = value[key]
  return typeof field === "string" ? field : null
}

const hasDependency = (packageJson: PackageJsonShape | null, name: string): boolean => {
  if (packageJson === null) return false
  const deps = isRecord(packageJson.dependencies) ? packageJson.dependencies : {}
  const devDeps = isRecord(packageJson.devDependencies) ? packageJson.devDependencies : {}
  return name in deps || name in devDeps
}

const versionMajor = (version: string | null): number | null => {
  if (version === null) return null
  const match = version.match(/^v?(\d+)/)
  return match === null ? null : Number(match[1])
}

const compareVersions = (
  current: { readonly major: number; readonly minor: number; readonly patch: number },
  minimum: { readonly major: number; readonly minor: number; readonly patch: number },
): number => {
  if (current.major !== minimum.major) return current.major - minimum.major
  if (current.minor !== minimum.minor) return current.minor - minimum.minor
  return current.patch - minimum.patch
}

const parseVersion = (
  value: string,
): { readonly major: number; readonly minor: number; readonly patch: number } | null => {
  const match = value.match(/v?(\d+)(?:\.(\d+))?(?:\.(\d+))?/)
  if (match === null) return null
  return {
    major: Number(match[1]),
    minor: match[2] === undefined ? 0 : Number(match[2]),
    patch: match[3] === undefined ? 0 : Number(match[3]),
  }
}

const parseMinimumNode = (
  range: string,
): { readonly major: number; readonly minor: number; readonly patch: number } | null => {
  const minimum = range.match(/>=\s*(\d+)(?:\.(\d+))?(?:\.(\d+))?/)
  if (minimum === null) return null
  return {
    major: Number(minimum[1]),
    minor: minimum[2] === undefined ? 0 : Number(minimum[2]),
    patch: minimum[3] === undefined ? 0 : Number(minimum[3]),
  }
}

export const nodeRequirementOf = (
  packageJson: PackageJsonShape | null,
  current = process.version,
): NodeRequirement => {
  const range =
    packageJson === null || !isRecord(packageJson.engines)
      ? null
      : typeof packageJson.engines.node === "string"
        ? packageJson.engines.node
        : null
  if (range === null) return { range, current, satisfied: null }

  const minimum = parseMinimumNode(range)
  const currentVersion = parseVersion(current)
  if (minimum === null || currentVersion === null) {
    return { range, current, satisfied: null }
  }
  return {
    range,
    current,
    satisfied: compareVersions(currentVersion, minimum) >= 0,
  }
}

const packageManagerFromField = (field: unknown): PackageManagerName | null => {
  if (typeof field !== "string") return null
  const name = field.split("@")[0]
  return name === "pnpm" || name === "npm" || name === "yarn" || name === "bun"
    ? name
    : null
}

export const detectPackageManager = (input: {
  readonly packageJson: PackageJsonShape | null
  readonly lockfiles: ReadonlyArray<LockfileInfo>
}): PackageManagerDetection => {
  const packageManagerField =
    typeof input.packageJson?.packageManager === "string"
      ? input.packageJson.packageManager
      : null
  const fromField = packageManagerFromField(packageManagerField)
  const warnings: Array<string> = []

  if (input.lockfiles.length > 1) {
    warnings.push(
      `Multiple lockfiles found: ${input.lockfiles.map((lock) => lock.file).join(", ")}.`,
    )
  }

  if (fromField !== null) {
    const mismatched = input.lockfiles.filter((lock) => lock.name !== fromField)
    if (mismatched.length > 0) {
      warnings.push(
        `packageManager says ${fromField}, but ${mismatched.map((lock) => lock.file).join(", ")} also exists.`,
      )
    }
    return {
      name: fromField,
      source: `packageManager (${packageManagerField})`,
      packageManagerField,
      lockfiles: input.lockfiles,
      warnings,
    }
  }

  const lockfileChoice = PACKAGE_MANAGER_PRIORITY.flatMap((name) =>
    input.lockfiles.filter((lock) => lock.name === name),
  )[0]
  if (lockfileChoice !== undefined) {
    warnings.push("package.json has no packageManager field.")
    return {
      name: lockfileChoice.name,
      source: lockfileChoice.file,
      packageManagerField,
      lockfiles: input.lockfiles,
      warnings,
    }
  }

  warnings.push("No lockfile found; falling back to npm.")
  return {
    name: "npm",
    source: "fallback",
    packageManagerField,
    lockfiles: input.lockfiles,
    warnings,
  }
}

export const installCommandFor = (
  manager: PackageManagerName | null,
  lockfiles: ReadonlyArray<LockfileInfo>,
): string => {
  const hasLock = (name: PackageManagerName): boolean =>
    lockfiles.some((lock) => lock.name === name)
  switch (manager) {
    case "pnpm":
      return hasLock("pnpm")
        ? "corepack pnpm install --frozen-lockfile"
        : "corepack pnpm install"
    case "npm":
      return hasLock("npm") ? "npm ci" : "npm install"
    case "yarn":
      return hasLock("yarn")
        ? "corepack yarn install --immutable"
        : "corepack yarn install"
    case "bun":
      return hasLock("bun") ? "bun install --frozen-lockfile" : "bun install"
    case null:
      return "npm install"
  }
}

export const devCommandFor = (manager: PackageManagerName | null): string => {
  switch (manager) {
    case "pnpm":
      return "corepack pnpm dev"
    case "npm":
      return "npm run dev"
    case "yarn":
      return "corepack yarn dev"
    case "bun":
      return "bun run dev"
    case null:
      return "npm run dev"
  }
}

const parsePackageJson = (raw: string): {
  readonly packageJson: PackageJsonShape | null
  readonly error: string | null
} => {
  try {
    const parsed: unknown = JSON.parse(raw)
    return isRecord(parsed)
      ? { packageJson: parsed, error: null }
      : { packageJson: null, error: "package.json is not an object" }
  } catch (error) {
    return {
      packageJson: null,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

const readOptionalText = (
  projectDir: string,
  relativePath: string,
): Effect.Effect<string | null, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const file = path.join(projectDir, relativePath)
    const exists = yield* fs.exists(file).pipe(Effect.orElseSucceed(() => false))
    if (!exists) return null
    return yield* fs.readFileString(file).pipe(Effect.orElseSucceed(() => null))
  })

const pathExists = (
  projectDir: string,
  relativePath: string,
): Effect.Effect<boolean, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    return yield* fs
      .exists(path.join(projectDir, relativePath))
      .pipe(Effect.orElseSucceed(() => false))
  })

const parseAllowBuilds = (yaml: string | null): Readonly<Record<string, boolean>> => {
  if (yaml === null) return {}
  const allowBuilds: Record<string, boolean> = {}
  let inAllowBuilds = false
  for (const line of yaml.split("\n")) {
    if (/^allowBuilds:\s*$/.test(line)) {
      inAllowBuilds = true
      continue
    }
    if (inAllowBuilds && /^\S/.test(line) && line.trim().length > 0) {
      break
    }
    if (!inAllowBuilds) continue
    const match = line.match(/^\s{2}["']?([^"':]+)["']?:\s*(true|false)\s*$/)
    if (match !== null) {
      allowBuilds[match[1]] = match[2] === "true"
    }
  }
  return allowBuilds
}

const gitignoreHasHeysegment = (gitignore: string | null): boolean =>
  gitignore !== null &&
  gitignore
    .split("\n")
    .some((line) => line.trim() === ".heysegment" || line.trim() === ".heysegment/")

const readInstalledVersion = (
  projectDir: string,
  packageName: "astro" | "vite",
): Effect.Effect<string | null, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const direct = yield* readOptionalText(projectDir, `node_modules/${packageName}/package.json`)
    if (direct !== null) {
      return stringField(parsePackageJson(direct).packageJson, "version")
    }

    const fs = yield* FileSystem.FileSystem
    const path = yield* Path.Path
    const store = path.join(projectDir, "node_modules", ".pnpm")
    const entries = yield* fs.readDirectory(store).pipe(Effect.orElseSucceed(() => []))
    const prefix = `${packageName}@`
    for (const entry of entries) {
      if (!entry.startsWith(prefix)) continue
      const raw = yield* fs
        .readFileString(path.join(store, entry, "node_modules", packageName, "package.json"))
        .pipe(Effect.orElseSucceed(() => null))
      if (raw !== null) {
        return stringField(parsePackageJson(raw).packageJson, "version")
      }
    }
    return null
  })

const neededPnpmBuildApprovals = (
  packageJson: PackageJsonShape | null,
  pnpmLock: string | null,
): ReadonlyArray<string> =>
  PNPM_BUILD_PACKAGES.filter((name) => {
    if (hasDependency(packageJson, name)) return true
    return pnpmLock?.includes(`${name}@`) ?? false
  })

export const readProjectSetup = (
  projectDir: string,
): Effect.Effect<ProjectSetupInfo, never, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const packageJsonRaw = yield* readOptionalText(projectDir, "package.json")
    const parsed =
      packageJsonRaw === null
        ? { packageJson: null, error: null }
        : parsePackageJson(packageJsonRaw)
    const presentLockfiles = yield* Effect.all(
      LOCKFILES.map((lock) =>
        pathExists(projectDir, lock.file).pipe(
          Effect.map((exists): LockfileInfo | null => (exists ? lock : null)),
        ),
      ),
    )
    const lockfiles = presentLockfiles.filter((lock): lock is LockfileInfo => lock !== null)
    const pnpmWorkspaceRaw = yield* readOptionalText(projectDir, "pnpm-workspace.yaml")
    const pnpmLockRaw = yield* readOptionalText(projectDir, "pnpm-lock.yaml")
    const gitignoreRaw = yield* readOptionalText(projectDir, ".gitignore")
    const [hasNodeModules, hasSessionFile, astroVersion, viteVersion] = yield* Effect.all([
      pathExists(projectDir, "node_modules"),
      pathExists(projectDir, ".heysegment/session.json"),
      readInstalledVersion(projectDir, "astro"),
      readInstalledVersion(projectDir, "vite"),
    ])

    return {
      projectDir,
      packageJson: parsed.packageJson,
      packageJsonParseError: parsed.error,
      packageManager: detectPackageManager({
        packageJson: parsed.packageJson,
        lockfiles,
      }),
      nodeRequirement: nodeRequirementOf(parsed.packageJson),
      hasNodeModules,
      hasSessionFile,
      gitignoreHasHeysegment: gitignoreHasHeysegment(gitignoreRaw),
      pnpmWorkspace: {
        exists: pnpmWorkspaceRaw !== null,
        allowBuilds: parseAllowBuilds(pnpmWorkspaceRaw),
      },
      neededPnpmBuildApprovals: neededPnpmBuildApprovals(parsed.packageJson, pnpmLockRaw),
      installedVersions: {
        astro: astroVersion,
        vite: viteVersion,
      },
    }
  })

const hasScript = (
  packageJson: PackageJsonShape | null,
  name: string,
): boolean =>
  packageJson !== null && isRecord(packageJson.scripts) && typeof packageJson.scripts[name] === "string"

export const renderPostPullSetup = (setup: ProjectSetupInfo): string => {
  const manager = setup.packageManager.name
  const install = installCommandFor(manager, setup.packageManager.lockfiles)
  const dev = devCommandFor(manager)
  const lines: Array<string> = ["", "Next steps:"]

  if (setup.nodeRequirement.range !== null) {
    lines.push(`  Node: ${setup.nodeRequirement.range} (current ${setup.nodeRequirement.current})`)
  } else {
    lines.push(`  Node: no project engine declared (current ${setup.nodeRequirement.current})`)
  }
  lines.push(`  Package manager: ${manager ?? "unknown"} from ${setup.packageManager.source}`)
  lines.push(`  Install: ${install}`)
  if (hasScript(setup.packageJson, "dev")) {
    lines.push(`  Dev server: ${dev}`)
  }
  lines.push("  Validate: heysegment validate")
  lines.push("  Push: heysegment push")

  const warnings: Array<string> = []
  if (setup.nodeRequirement.satisfied === false && setup.nodeRequirement.range !== null) {
    warnings.push(
      `Current Node ${setup.nodeRequirement.current} does not satisfy ${setup.nodeRequirement.range}.`,
    )
  }
  warnings.push(...setup.packageManager.warnings)

  const missingApprovals = setup.neededPnpmBuildApprovals.filter(
    (name) => setup.pnpmWorkspace.allowBuilds[name] !== true,
  )
  if (manager === "pnpm" && missingApprovals.length > 0) {
    warnings.push(
      `pnpm build-script approvals missing for: ${missingApprovals.join(", ")}.`,
    )
  }

  if (warnings.length > 0) {
    lines.push("")
    lines.push("Setup warnings:")
    for (const warning of warnings) {
      lines.push(`  - ${warning}`)
    }
  }

  lines.push("")
  lines.push(
    "Session note: .heysegment/session.json is project-local identity only; the token is stored in ~/.heysegment/credentials.json. .heysegment/ is ignored and excluded from pushes.",
  )
  return lines.join("\n")
}

const legacyPnpmSettings = (packageJson: PackageJsonShape | null): ReadonlyArray<string> => {
  const pnpm = packageJson?.pnpm
  if (!isRecord(pnpm)) return []
  return ["onlyBuiltDependencies", "neverBuiltDependencies", "ignoredBuiltDependencies"].filter(
    (name) => name in pnpm,
  )
}

export const buildDoctorChecks = (setup: ProjectSetupInfo): ReadonlyArray<DoctorCheck> => {
  const checks: Array<DoctorCheck> = []

  if (setup.packageJson === null) {
    checks.push({
      name: "package.json",
      status: "fail",
      summary:
        setup.packageJsonParseError === null
          ? "No package.json found."
          : `Could not parse package.json: ${setup.packageJsonParseError}`,
      details: [],
    })
  } else {
    checks.push({
      name: "package.json",
      status: "ok",
      summary: "Project manifest found.",
      details: [],
    })
  }

  const node = setup.nodeRequirement
  checks.push({
    name: "Node",
    status: node.satisfied === false ? "fail" : node.range === null ? "warn" : "ok",
    summary:
      node.range === null
        ? `No engines.node declared (current ${node.current}).`
        : node.satisfied === false
          ? `Current ${node.current} does not satisfy ${node.range}.`
          : `Current ${node.current} satisfies ${node.range}.`,
    details:
      node.satisfied === false
        ? ["Use Node 22+ before installing dependencies or running Astro."]
        : [],
  })

  checks.push({
    name: "Package manager",
    status: setup.packageManager.warnings.length > 0 ? "warn" : "ok",
    summary: `${setup.packageManager.name ?? "unknown"} selected from ${setup.packageManager.source}.`,
    details: setup.packageManager.warnings,
  })

  checks.push({
    name: "Dependencies",
    status: setup.hasNodeModules ? "ok" : "warn",
    summary: setup.hasNodeModules
      ? "node_modules is present."
      : "Dependencies are not installed.",
    details: setup.hasNodeModules
      ? []
      : [installCommandFor(setup.packageManager.name, setup.packageManager.lockfiles)],
  })

  const legacySettings = legacyPnpmSettings(setup.packageJson)
  const missingApprovals = setup.neededPnpmBuildApprovals.filter(
    (name) => setup.pnpmWorkspace.allowBuilds[name] !== true,
  )
  if (setup.packageManager.name === "pnpm" || setup.packageManager.lockfiles.some((lock) => lock.name === "pnpm")) {
    const details = [
      ...legacySettings.map(
        (name) => `${name} is legacy pnpm config; use allowBuilds in pnpm-workspace.yaml.`,
      ),
      ...missingApprovals.map((name) => `allowBuilds.${name} is not true.`),
    ]
    checks.push({
      name: "pnpm build approvals",
      status: details.length > 0 ? "warn" : "ok",
      summary:
        details.length > 0
          ? "pnpm 11 may block install-time build scripts."
          : "Required build-script approvals are configured.",
      details,
    })
  }

  checks.push({
    name: "Session files",
    status: setup.hasSessionFile && !setup.gitignoreHasHeysegment ? "warn" : "ok",
    summary: setup.hasSessionFile
      ? ".heysegment/session.json is present; credentials remain in ~/.heysegment/credentials.json."
      : "No project-local session file found.",
    details: setup.hasSessionFile && !setup.gitignoreHasHeysegment
      ? [".heysegment/ is not listed in .gitignore."]
      : [".heysegment/ is excluded from heysegment push archives."],
  })

  if (hasDependency(setup.packageJson, "astro")) {
    const astroMajor = versionMajor(setup.installedVersions.astro)
    const viteMajor = versionMajor(setup.installedVersions.vite)
    const mismatch = astroMajor !== null && astroMajor <= 6 && viteMajor !== null && viteMajor >= 8
    checks.push({
      name: "Astro/Vite",
      status: mismatch ? "warn" : setup.installedVersions.vite === null ? "warn" : "ok",
      summary: mismatch
        ? `Installed Astro ${setup.installedVersions.astro} with Vite ${setup.installedVersions.vite}.`
        : setup.installedVersions.vite === null
          ? "Cannot inspect installed Vite until dependencies are installed."
          : `Installed Astro ${setup.installedVersions.astro} with Vite ${setup.installedVersions.vite}.`,
      details: mismatch
        ? [
            "Reinstall with the detected lockfile, and keep Vite pinned to the Astro-compatible major.",
          ]
        : [],
    })
  }

  return checks
}

export const renderDoctorChecks = (checks: ReadonlyArray<DoctorCheck>): string => {
  const lines = ["heysegment doctor"]
  for (const check of checks) {
    const marker = check.status === "ok" ? "✓" : check.status === "warn" ? "!" : "✗"
    lines.push(`${marker} ${check.name}: ${check.summary}`)
    for (const detail of check.details) {
      lines.push(`  - ${detail}`)
    }
  }
  return lines.join("\n")
}

export const runDoctorProgram = (input: {
  readonly dir: string
  readonly json: boolean
}): Effect.Effect<void, OperationFailed | UsageError, FileSystem.FileSystem | Path.Path> =>
  resolveProjectDir(input.dir).pipe(
    Effect.flatMap(readProjectSetup),
    Effect.flatMap((setup) => {
      const checks = buildDoctorChecks(setup)
      const failed = checks.filter((check) => check.status === "fail")
      const payload = {
        ok: failed.length === 0,
        projectDir: setup.projectDir,
        checks,
      }
      return Effect.gen(function* () {
        if (input.json) {
          yield* Console.log(JSON.stringify(payload, null, 2))
        } else {
          yield* Console.log(renderDoctorChecks(checks))
        }
        if (failed.length > 0) {
          return yield* Effect.fail(
            new OperationFailed({ failed: failed.map((check) => check.name) }),
          )
        }
      })
    }),
  )
